import { useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { AxiosError } from 'axios';

import { ThemedText } from '@/components/ThemedText';
import { useScale } from '@/hooks/useScale';
import { getTvUuid, setAccessToken } from '@/services/auth/authStorage';
import { loginTvWithEmailAndPassword } from '@/services/api/tvLoginApi';
import { colors } from '@/constants/colors';
import DefaultBackground from '@/components/DefaultBackground';
import Logo from '@/components/Logo';

type Props = {
  onBack: () => void;
};

export default function EmailPasswordLoginScreen({ onBack }: Props) {
  const scale = useScale();
  const styles = useStyles();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSubmit =
    email.trim().length > 0 && password.length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await loginTvWithEmailAndPassword(
        email.trim(),
        password,
        getTvUuid(),
      );

      if (response.success && response.data?.access_token) {
        setAccessToken(response.data.access_token);
        return;
      }

      setErrorMessage(response.error ?? 'Sign in failed. Please try again.');
    } catch (err) {
      let message = 'Could not connect. Please try again.';
      if (err instanceof AxiosError) {
        const data = err.response?.data as { error?: string } | undefined;
        if (data?.error) {
          message = data.error;
        }
      }
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={styles.container}>
      <DefaultBackground />

      <View style={styles.logoContainer}>
        <Logo height={38 * scale} scaleProportionally />
      </View>

      <View style={styles.centeredContainer}>
        <ThemedText type="title" lightColor="#fff" darkColor="#fff">
          Sign in with email
        </ThemedText>
        <ThemedText style={styles.description}>
          Enter the email and password for your HiNote Life account.
        </ThemedText>

        <View style={styles.form}>
          <TextInput
            style={styles.input}
            value={email}
            onChangeText={setEmail}
            placeholder="Email"
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            autoFocus={Platform.OS !== 'ios'}
            editable={!isSubmitting}
          />
          <TextInput
            style={styles.input}
            value={password}
            onChangeText={setPassword}
            placeholder="Password"
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
            secureTextEntry
            autoCapitalize="none"
            autoCorrect={false}
            editable={!isSubmitting}
            onSubmitEditing={handleSubmit}
          />

          {errorMessage && (
            <ThemedText style={styles.errorText}>{errorMessage}</ThemedText>
          )}

          <Pressable
            style={({ focused }) => [
              styles.primaryButton,
              focused && styles.primaryButtonFocused,
              !canSubmit && styles.primaryButtonDisabled,
            ]}
            onPress={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <ThemedText style={styles.primaryButtonText}>Sign In</ThemedText>
            )}
          </Pressable>

          <Pressable
            style={({ focused }) => [
              styles.secondaryButton,
              focused && styles.secondaryButtonFocused,
            ]}
            onPress={onBack}
            disabled={isSubmitting}
          >
            <ThemedText style={styles.secondaryButtonText}>
              Back to pair code
            </ThemedText>
          </Pressable>

          <ThemedText style={styles.poweredByHint}>
            HiNote Life - Powered by fit52
          </ThemedText>
        </View>
      </View>
    </View>
  );
}

function useStyles() {
  const scale = useScale();

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.brand.primary,
    },
    logoContainer: {
      alignItems: 'center',
      paddingTop: 32 * scale,
    },
    centeredContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24 * scale,
    },
    description: {
      marginTop: 12 * scale,
      fontSize: 16 * scale,
      color: 'rgba(255, 255, 255, 0.85)',
      textAlign: 'center',
      maxWidth: 500 * scale,
    },
    form: {
      marginTop: 24 * scale,
      width: 420 * scale,
      gap: 12 * scale,
    },
    input: {
      paddingVertical: 14 * scale,
      paddingHorizontal: 18 * scale,
      fontSize: 16 * scale,
      borderRadius: 12 * scale,
      borderWidth: 2,
      borderColor: 'rgba(255, 255, 255, 0.25)',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      // On Apple TV especially in the full screen system dialog where you type the value, using white as color
      // resulted in poor constrast. So we're just using system defaults.
      color: Platform.OS === 'ios' ? undefined : '#fff',
    },
    errorText: {
      fontSize: 14 * scale,
      color: '#ffb3b3',
      textAlign: 'center',
    },
    primaryButton: {
      marginTop: 8 * scale,
      paddingVertical: 14 * scale,
      paddingHorizontal: 32 * scale,
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      borderRadius: 12 * scale,
      borderWidth: 2,
      borderColor: 'rgba(255, 255, 255, 0.4)',
      alignItems: 'center',
    },
    primaryButtonFocused: {
      borderColor: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.35)',
    },
    primaryButtonDisabled: {
      opacity: 0.5,
    },
    primaryButtonText: {
      color: '#fff',
      fontSize: 18 * scale,
      fontWeight: '700',
    },
    secondaryButton: {
      marginTop: 4 * scale,
      paddingVertical: 10 * scale,
      paddingHorizontal: 20 * scale,
      borderRadius: 8 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
      alignItems: 'center',
    },
    secondaryButtonFocused: {
      borderColor: 'rgba(255, 255, 255, 0.6)',
    },
    secondaryButtonText: {
      color: 'rgba(255, 255, 255, 0.75)',
      fontSize: 16 * scale,
      fontWeight: '600',
      textDecorationLine: 'underline',
    },
    poweredByHint: {
      fontSize: 14 * scale,
      color: 'rgba(255,255,255,0.8)',
      textAlign: 'center',
      marginTop: 6 * scale,
    },
  });
}
